import { useState, useMemo } from 'react';
import {
  ClipboardList,
  Plus,
  Link2,
  Copy,
  Trash2,
  Loader2,
  Check,
  X,
  Send,
  ExternalLink,
  Clock,
  CheckCircle2,
  XCircle,
  Package,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCashierCounts, type CashierCount } from '@/hooks/useCashierCounts';
import { useCashierCountRequests } from '@/hooks/useCashierCountRequests';
import { useAuth } from '@/hooks/useAuth';
import { useTeam } from '@/hooks/useTeam';
import { useLocationStore } from '@/store/location';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getShareUrl(shortCode: string): string {
  return `${window.location.origin}/c/${shortCode}`;
}

function CountCard({
  count,
  copiedToken,
  onCopyLink,
  onOpenSendReport,
  onDelete,
}: {
  count: CashierCount;
  copiedToken: string | null;
  onCopyLink: (shortCode: string) => void;
  onOpenSendReport: (token: string, email?: string | null) => void;
  onDelete: (id: string) => void;
}) {
  const itemCount = count.items?.length || 0;
  const filledItems = (count.items || []).filter(
    (i) => i.opening_qty !== null || i.received_qty !== null || i.closing_qty !== null
  ).length;

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-sm">
              {count.cashier_name ? `${count.cashier_name}'s Count` : 'Unnamed Count'}
            </h3>
            <Badge
              variant={count.status === 'completed' ? 'default' : 'secondary'}
              className={cn(
                'text-[10px]',
                count.status === 'completed' && 'bg-green-600 hover:bg-green-700'
              )}
            >
              {count.status === 'completed'
                ? 'Completed'
                : count.status === 'in_progress'
                  ? 'In Progress'
                  : 'Draft'}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {count.location_name} &middot; {formatDate(count.created_at)}
            {count.notes && <> &middot; {count.notes}</>}
          </p>
          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            <span>{filledItems}/{itemCount} items counted</span>
            <span className="flex items-center gap-1">
              {count.opening_locked ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : (
                <X className="h-3 w-3 text-muted-foreground/40" />
              )}
              Opening
            </span>
            <span className="flex items-center gap-1">
              {count.received_locked ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : (
                <X className="h-3 w-3 text-muted-foreground/40" />
              )}
              Received
            </span>
            <span className="flex items-center gap-1">
              {count.closing_locked ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : (
                <X className="h-3 w-3 text-muted-foreground/40" />
              )}
              Closing
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1 border-t">
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          onClick={() => onCopyLink(count.short_code)}
        >
          {copiedToken === count.short_code ? (
            <Check className="h-3 w-3 mr-1.5 text-green-500" />
          ) : (
            <Copy className="h-3 w-3 mr-1.5" />
          )}
          Copy Link
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          asChild
        >
          <a
            href={getShareUrl(count.short_code)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink className="h-3 w-3 mr-1.5" />
            Open
          </a>
        </Button>
        {count.status === 'completed' && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={() => onOpenSendReport(count.share_token, count.report_email)}
          >
            <Send className="h-3 w-3 mr-1.5" />
            Send Report
          </Button>
        )}
        <div className="flex-1" />
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs text-destructive hover:text-destructive"
          onClick={() => onDelete(count.id)}
        >
          <Trash2 className="h-3 w-3 mr-1.5" />
          Delete
        </Button>
      </div>
    </div>
  );
}

export default function CashierCounts() {
  const { organization } = useAuth();
  const { currentLocation, locations, locationScope } = useLocationStore();
  const selectedLocationId = locationScope === 'single' ? currentLocation?.id : null;
  const { counts, isLoading, createCount, deleteCount } = useCashierCounts();
  const { requests, approveRequest, denyRequest } = useCashierCountRequests();
  const { teamMembers } = useTeam();
  const [showCreate, setShowCreate] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [copiedRequestLink, setCopiedRequestLink] = useState(false);

  // Create form state
  const [reportEmail, setReportEmail] = useState('');
  const [selectedCashierId, setSelectedCashierId] = useState('');
  const [createLocationId, setCreateLocationId] = useState('');
  const [notes, setNotes] = useState('');
  const [itemsText, setItemsText] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [autoPopulate, setAutoPopulate] = useState(true);

  // Send report dialog state
  const [sendReportToken, setSendReportToken] = useState<string | null>(null);
  const [sendReportEmail, setSendReportEmail] = useState('');
  const [isSendingReport, setIsSendingReport] = useState(false);

  // The effective location for creating counts: either the global selection or the dialog picker
  const effectiveCreateLocationId = selectedLocationId || createLocationId;

  // Filter team members to those with access to the effective create location
  const locationStaff = useMemo(() => {
    if (!teamMembers || !effectiveCreateLocationId) return [];
    return teamMembers.filter(
      (m) =>
        m.is_active &&
        m.location_access?.some((la) => la.location_id === effectiveCreateLocationId)
    );
  }, [teamMembers, effectiveCreateLocationId]);

  // Group counts by location when viewing all locations
  const countsByLocation = useMemo(() => {
    if (selectedLocationId) return null;
    const grouped = new Map<string, { locationName: string; counts: typeof counts }>();
    for (const count of counts) {
      const key = count.location_id;
      if (!grouped.has(key)) {
        grouped.set(key, { locationName: count.location_name || 'Unknown Location', counts: [] });
      }
      grouped.get(key)!.counts.push(count);
    }
    return grouped;
  }, [counts, selectedLocationId]);

  const handleCopyLink = (shortCode: string) => {
    navigator.clipboard.writeText(getShareUrl(shortCode));
    setCopiedToken(shortCode);
    toast.success('Share link copied to clipboard');
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleCreate = async () => {
    if (!effectiveCreateLocationId) {
      toast.error('Please select a location');
      return;
    }
    if (!organization?.id) {
      toast.error('Organization not found');
      return;
    }

    const items = autoPopulate
      ? []
      : itemsText
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean);

    if (!autoPopulate && items.length === 0) {
      toast.error('Add at least one item to the count');
      return;
    }

    setIsCreating(true);
    try {
      // Resolve cashier name from selected team member
      const cashierMember = selectedCashierId
        ? locationStaff.find((m) => m.id === selectedCashierId)
        : null;
      const cashierName = cashierMember
        ? [cashierMember.first_name, cashierMember.last_name].filter(Boolean).join(' ')
        : undefined;

      const result = await createCount.mutateAsync({
        locationId: effectiveCreateLocationId,
        organizationId: organization.id,
        reportEmail: reportEmail.trim() || undefined,
        cashierUserId: selectedCashierId || undefined,
        cashierName,
        items,
        notes: notes.trim() || undefined,
        autoPopulate,
      });

      toast.success('Cashier count created');
      setShowCreate(false);
      setReportEmail('');
      setSelectedCashierId('');
      setCreateLocationId('');
      setNotes('');
      setItemsText('');
      setAutoPopulate(true);

      // Auto-copy the link
      if (result?.short_code) {
        handleCopyLink(result.short_code);
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to create cashier count');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (countId: string) => {
    if (!confirm('Delete this cashier count? This cannot be undone.')) return;
    try {
      await deleteCount.mutateAsync(countId);
      toast.success('Cashier count deleted');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  const handleOpenSendReport = (token: string, existingEmail?: string | null) => {
    setSendReportToken(token);
    setSendReportEmail(existingEmail || '');
  };

  const handleConfirmSendReport = async () => {
    if (!sendReportToken) return;
    setIsSendingReport(true);
    try {
      const { error } = await supabase.functions.invoke('send-cashier-count-report', {
        body: {
          share_token: sendReportToken,
          ...(sendReportEmail.trim() ? { override_email: sendReportEmail.trim() } : {}),
        },
      });
      if (error) throw error;
      toast.success('Report sent');
      setSendReportToken(null);
      setSendReportEmail('');
    } catch (err: any) {
      toast.error(err.message || 'Failed to send report');
    } finally {
      setIsSendingReport(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Cashier Stock Counts</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Create shareable links for cashiers to track opening, received, and closing stock.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {organization?.id && (
            <Button
              variant="outline"
              onClick={() => {
                const url = `${window.location.origin}/request-count?org=${organization.id}`;
                navigator.clipboard.writeText(url);
                setCopiedRequestLink(true);
                toast.success('Request link copied to clipboard');
                setTimeout(() => setCopiedRequestLink(false), 2000);
              }}
            >
              {copiedRequestLink ? (
                <Check className="h-4 w-4 mr-2 text-green-500" />
              ) : (
                <Copy className="h-4 w-4 mr-2" />
              )}
              Copy Request Link
            </Button>
          )}
        <Dialog open={showCreate} onOpenChange={(open) => {
          setShowCreate(open);
          if (!open) {
            setCreateLocationId('');
            setSelectedCashierId('');
          }
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Count
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Cashier Stock Count</DialogTitle>
              <DialogDescription>
                Create a new stock count sheet. A shareable link will be generated that can be
                sent to anyone - no account needed.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              {!selectedLocationId && locations.length > 0 && (
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Select value={createLocationId} onValueChange={(v) => {
                    setCreateLocationId(v);
                    setSelectedCashierId('');
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a location..." />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.filter((l) => l.is_active !== false).map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Report Email (optional)</Label>
                <Input
                  type="email"
                  value={reportEmail}
                  onChange={(e) => setReportEmail(e.target.value)}
                  placeholder="manager@example.com"
                />
                <p className="text-xs text-muted-foreground">
                  The completed report will be emailed here. If left empty, it goes to the organization owner.
                </p>
              </div>

              {locationStaff.length > 0 && (
                <div className="space-y-2">
                  <Label>Cashier (optional)</Label>
                  <Select value={selectedCashierId} onValueChange={(v) => setSelectedCashierId(v === '__none__' ? '' : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a cashier..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {locationStaff.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {[m.first_name, m.last_name].filter(Boolean).join(' ')}{' '}
                          <span className="text-muted-foreground">({m.role})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Pre-assign this count to a specific cashier at this location.
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <Label className="flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Auto-populate from inventory
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Pre-fill items and opening quantities from current FOH stock.
                  </p>
                </div>
                <Switch checked={autoPopulate} onCheckedChange={setAutoPopulate} />
              </div>

              {!autoPopulate && (
                <div className="space-y-2">
                  <Label>Items (one per line)</Label>
                  <textarea
                    value={itemsText}
                    onChange={(e) => setItemsText(e.target.value)}
                    placeholder={"Jerk Chicken\nCurry Goat\nRice & Peas\nFried Chicken\nOxtail\nWater"}
                    rows={8}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter the items the cashier needs to count. They can also add items from the shared link.
                  </p>
                </div>
              )}

              {autoPopulate && (
                <div className="rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 p-3 text-xs text-blue-700 dark:text-blue-400">
                  All active, inventory-tracked products at this location will be included automatically.
                  Opening quantities will be set to the current Front of House stock levels.
                </div>
              )}

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g., Morning shift count"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreate} disabled={isCreating}>
                  {isCreating ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Link2 className="h-4 w-4 mr-2" />
                  )}
                  Create & Copy Link
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Pending Requests */}
      {requests.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Pending Requests
            <Badge variant="secondary" className="text-[10px]">{requests.length}</Badge>
          </h3>
          {requests.map((req) => (
            <div key={req.id} className="rounded-lg border bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 p-4 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-sm">{req.cashier_name}</h4>
                    <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 dark:text-amber-400">
                      Pending Approval
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {req.location_name}
                    {req.notes && <> &middot; {req.notes}</>}
                    {' '}&middot; {new Date(req.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button
                  size="sm"
                  className="h-8 text-xs bg-green-600 hover:bg-green-700"
                  onClick={async () => {
                    try {
                      await approveRequest.mutateAsync(req.approval_token);
                      toast.success(`Approved count for ${req.cashier_name}`);
                    } catch (err: any) {
                      toast.error(err.message || 'Failed to approve');
                    }
                  }}
                  disabled={approveRequest.isPending}
                >
                  {approveRequest.isPending ? (
                    <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-3 w-3 mr-1.5" />
                  )}
                  Approve
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs text-destructive hover:text-destructive"
                  onClick={async () => {
                    try {
                      await denyRequest.mutateAsync({ approvalToken: req.approval_token });
                      toast.success('Request denied');
                    } catch (err: any) {
                      toast.error(err.message || 'Failed to deny');
                    }
                  }}
                  disabled={denyRequest.isPending}
                >
                  <XCircle className="h-3 w-3 mr-1.5" />
                  Deny
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && counts.length === 0 && (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <ClipboardList className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-medium mb-1">No cashier counts yet</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Create a stock count and share the link with your cashiers.
          </p>
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create First Count
          </Button>
        </div>
      )}

      {/* Counts List */}
      {counts.length > 0 && countsByLocation && (
        <div className="space-y-6">
          {Array.from(countsByLocation.entries()).map(([locationId, { locationName, counts: locationCounts }]) => (
            <div key={locationId} className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
                {locationName}
                <Badge variant="secondary" className="ml-2 text-[10px]">
                  {locationCounts.length}
                </Badge>
              </h3>
              {locationCounts.map((count) => (
                <CountCard
                  key={count.id}
                  count={count}
                  copiedToken={copiedToken}
                  onCopyLink={handleCopyLink}
                  onOpenSendReport={handleOpenSendReport}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {counts.length > 0 && !countsByLocation && (
        <div className="space-y-3">
          {counts.map((count) => (
            <CountCard
              key={count.id}
              count={count}
              copiedToken={copiedToken}
              onCopyLink={handleCopyLink}
              onOpenSendReport={handleOpenSendReport}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Send Report Dialog */}
      <Dialog open={!!sendReportToken} onOpenChange={(open) => !open && setSendReportToken(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Report Email</DialogTitle>
            <DialogDescription>
              Choose who receives the report. Leave empty to use the default recipient
              (the report email set on the count, or the organization owner).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Send to (optional override)</Label>
              <Input
                type="email"
                value={sendReportEmail}
                onChange={(e) => setSendReportEmail(e.target.value)}
                placeholder="recipient@example.com"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSendReportToken(null)}>
                Cancel
              </Button>
              <Button onClick={handleConfirmSendReport} disabled={isSendingReport}>
                {isSendingReport ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
